import React from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import UploadZone from '../common/UploadZone';
import LoadingSpinner from '../common/LoadingSpinner';
import { stateOptions } from '../../constants';
import './ToolInterface.css';

/**
 * ToolInterface - Streamlined document analysis section
 */
export function ToolInterface({
  selectedState,
  onStateChange,
  file,
  onFileChange,
  onFileRemove,
  textInput,
  onTextChange,
  onTextSubmit,
  textUploaded,
  status,
  progressMessage,
  disclaimerChecked,
  onDisclaimerChange,
  onAnalyze,
  onViewReport,
  onRestart,
}) {
  const { t } = useTranslation();
  const isProcessing = status === 'uploading' || status === 'polling';
  const isSuccess = status === 'success';
  const canAnalyze = (file || textUploaded) && disclaimerChecked && selectedState && !isProcessing;

  return (
    <section className="tool-interface-section" id="tool">
      <div className="tool-interface-container">
        {/* GET STARTED pill badge */}
        <div className="get-started-badge">{t('toolInterface.getStarted')}</div>

        {/* Heading */}
        <h2 className="tool-interface-heading">
          {t('toolInterface.heading')}
        </h2>

        {/* Downward chevron */}
        <div className="chevron-down">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </div>

        {/* Upload Zone */}
        <UploadZone
          file={file}
          onFileChange={onFileChange}
          onFileRemove={onFileRemove}
          textInput={textInput}
          onTextChange={onTextChange}
          onTextSubmit={onTextSubmit}
          textUploaded={textUploaded}
          disabled={isProcessing}
        />

        {/* Inline controls row */}
        <div className="tool-controls-row">
          {/* State Dropdown */}
          <div className="state-dropdown-inline">
            <label htmlFor="state-select" className="state-dropdown-label">
              {t('toolInterface.yourState')}
            </label>
            <Select
              inputId="state-select"
              value={selectedState}
              onChange={onStateChange}
              options={stateOptions}
              isDisabled={isProcessing}
              className="state-select-compact"
              classNamePrefix="state-select"
              placeholder={t('toolInterface.selectState')}
              isSearchable
            />
          </div>

          {/* Disclaimer Checkbox */}
          <label className="disclaimer-inline">
            <input
              type="checkbox"
              checked={disclaimerChecked}
              onChange={(e) => onDisclaimerChange(e.target.checked)}
              disabled={isProcessing}
            />
            <span>{t('toolInterface.notLegalAdvice')}</span>
          </label>

          {/* Analyze Button */}
          {!isSuccess && !isProcessing && (
            <button
              className="analyze-button-compact"
              onClick={onAnalyze}
              disabled={!canAnalyze}
            >
              {t('common.analyze')}
            </button>
          )}

          {isSuccess && (
            <>
              <button className="view-report-button-compact" onClick={onViewReport}>
                {t('common.viewReport')}
              </button>
              <button className="start-over-button-compact" onClick={onRestart}>
                {t('common.startOver')}
              </button>
            </>
          )}
        </div>

        {/* Processing Indicator */}
        {isProcessing && (
          <div className="processing-inline">
            <LoadingSpinner message={progressMessage} />
          </div>
        )}

        {/* Trust Badge - subtle */}
        <div className="trust-badge-subtle">
          <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
            <path d="M15 8.5V6.5C15 3.73858 12.7614 1.5 10 1.5C7.23858 1.5 5 3.73858 5 6.5V8.5M10 12V14M7 18.5H13C14.1046 18.5 15 17.6046 15 16.5V10.5C15 9.39543 14.1046 8.5 13 8.5H7C5.89543 8.5 5 9.39543 5 10.5V16.5C5 17.6046 5.89543 18.5 7 18.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <span>{t('toolInterface.trustBadge')}</span>
        </div>
      </div>
    </section>
  );
}
